import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CHAPTERS, IMAGES } from "@/data/site";

export default function Story() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div data-testid="story-page" className="pt-28 sm:pt-36">
      <header className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-harissa mb-5">Die Idee</p>
        <h1 className="font-display uppercase text-[15vw] sm:text-[12vw] leading-[0.82] tracking-tight">
          <MaskLines lines={["Afrika, in der", <span key="b" className="text-stroke-harissa">Bar nebenan.</span>]} />
        </h1>
      </header>

      {/* Intro asymmetric */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <div className="aspect-[4/5] overflow-hidden border border-line">
                <img src={IMAGES.portrait} alt="Afrikanische Esskultur" className="w-full h-full object-cover contrast-110" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex flex-col justify-center">
            <Reveal delay={0.1}>
              <p className="font-sans text-xl sm:text-2xl leading-relaxed">
                Banga Bowls bringt afrikanisches Street Food dorthin, wo am Abend ohnehin alle sind: in die Bar. Kein steifes Restaurant, keine endlose Karte, dafür <span className="text-harissa">ein paar Gerichte, die richtig gut sind</span>, in schönen Boxen serviert.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-sans text-muted mt-8">
                Gegessen wird am Tresen, auf dem Sofa oder im Stehen, mit einem Drink in der Hand. Auf dem Teller trifft Nordafrika auf Westafrika: die Schärfe einer Merguez, der Komfort von knusprigem Poulet, der Duft von Jollof und gebratenen Kochbananen. Würzig, farbig und gemacht, um geteilt zu werden.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee stroke={false} />

      {/* Parallax atmosphere */}
      <section ref={ref} className="relative h-[70vh] overflow-hidden border-y border-line my-0">
        <motion.div style={{ y }} className="absolute inset-0 -top-[10%] h-[120%]">
          <img src={IMAGES.barNeon} alt="Warmes Licht, Neon, Bar" className="w-full h-full object-cover brightness-90 contrast-125" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-base via-base/40 to-transparent" />
        <div className="relative z-10 h-full flex items-end">
          <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 pb-14">
            <Reveal>
              <h2 className="font-head font-[900] text-3xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] max-w-3xl">
                Warmes Licht, gute Musik, Gewürze in der Luft.
              </h2>
              <p className="font-sans text-muted mt-6 max-w-xl">
                Banga Bowls ist laut genug, um lebendig zu sein, und gemütlich genug, um zu bleiben. Das Essen ist der Grund herzukommen, die Bar der Grund, noch einen draufzulegen.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 sm:py-32">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-harissa mb-14">Der Abend · in drei Kapiteln</p>
        <div className="space-y-px bg-line border-y border-line">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08} className="bg-base">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12 group hover:bg-surface transition-colors duration-300 px-1 sm:px-4">
                <div className="md:col-span-4">
                  <span className="font-display text-7xl sm:text-8xl text-stroke-harissa group-hover:[color:#FF3B30] group-hover:[-webkit-text-stroke:0] transition-all">{c.n}</span>
                </div>
                <div className="md:col-span-6 md:col-start-6">
                  <h3 className="font-head font-[800] text-3xl uppercase">{c.title}</h3>
                  <p className="font-sans text-muted mt-4 text-lg">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
